import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'src/components/atoms';
import { Modal as LegacyModal } from 'src/components/atomsLegacy';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  handleErrorToast,
  Modal,
  SpinnerButton,
} from 'src/components/molecules';
import { File } from 'src/components/moleculesLegacy';
import {
  fetchDraftReport,
  updateCurrentEvent,
} from 'src/creators/MCC.creators';
import { initialEventState } from 'src/reducers/ManualCase';
import { isSameOrBefore } from 'src/utils/time';
import { AppDispatch } from 'src/store';
import * as Types from '../../../types';
import { AddEventForm } from '../AddEventForm';
import {
  Body,
  BottomRow,
  ButtonContainer,
  Header,
  Subtitle,
  Title,
} from './AddEventModal.styles';
import { addNonExistingEvent } from './helpers/addNonExistingEvent';
import { getEvidence } from './helpers/getEvidence';
import { updateExistingEvent } from './helpers/updateExistingEvent';

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setIsAddingPerpetrator: (isAddingPerpetrator: boolean) => void;
  reportID: string;
  editingEvent: boolean;
  files: File[];
  setFiles: (files: File[]) => void;
};

export const AddEventModal = ({
  open,
  setOpen,
  setIsAddingPerpetrator,
  editingEvent,
  reportID,
  files,
  setFiles,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentReport =
    useSelector(
      (state: Types.State) =>
        state?.manualCaseCreation?.mccReducer?.currentReport,
    ) ?? {};
  const currentEvent =
    useSelector(
      (state: Types.State) =>
        state?.manualCaseCreation?.mccReducer?.currentEvent,
    ) ?? initialEventState;

  useEffect(() => {
    const getAllEvidence = async () => {
      await getEvidence(reportID, currentEvent.files, setFiles);
    };
    if (currentEvent.id) {
      getAllEvidence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent.files, currentEvent.id, open, reportID]);

  const [showStopEditing, setShowStopEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeModal = async () => {
    await dispatch(updateCurrentEvent(initialEventState));
    setFiles([]);
    setShowStopEditing(false);
    setOpen(false);
  };

  return (
    <LegacyModal
      open={open}
      setOpen={setOpen}
      onClose={() => {
        setShowStopEditing(true);
        setIsSubmitting(false);
      }}
      data-testid="add-event-modal"
    >
      <Header>
        <Title>Add Event</Title>
        <Subtitle>Enter and submit event information</Subtitle>
      </Header>
      <Body>
        <Modal
          size="medium"
          title="Leave event creation?"
          description="Your changes will not be saved."
          open={showStopEditing}
          setOpen={setShowStopEditing}
          cancelButtonText="Stay"
          confirmButtonText="Leave"
          onCancel={() => setShowStopEditing(false)}
          onConfirm={closeModal}
          data-testid="leave-event-creation-modal"
        />
        <AddEventForm
          reportID={reportID}
          setIsAddingPerpetrator={setIsAddingPerpetrator}
          files={files}
          currentEvent={currentEvent}
        />
        <BottomRow>
          <ButtonContainer>
            <Button
              onClick={() => {
                setShowStopEditing(true);
                setIsSubmitting(false);
              }}
              data-testid="cancel-add-event"
              variant="secondary"
              size="medium"
              width="120px"
            >
              Back
            </Button>
            <SpinnerButton
              variant="primary"
              size="medium"
              width="120px"
              loading={isSubmitting}
              onClick={async () => {
                setIsSubmitting(true);
                if (
                  !!currentEvent.date.value &&
                  !isSameOrBefore(currentEvent.date.value, new Date())
                ) {
                  handleErrorToast({
                    toastMessage: `Event date cannot be in the future`,
                  });
                  setIsSubmitting(false);
                  return;
                }
                if (!currentEvent.description.value) {
                  handleErrorToast({
                    toastMessage: `"What happened, in the reporter's own words?" cannot be empty`,
                  });
                } else {
                  if (currentReport) {
                    editingEvent
                      ? await updateExistingEvent(
                          currentReport,
                          reportID,
                          currentEvent,
                          currentEvent.id,
                          dispatch,
                        )
                      : await addNonExistingEvent(
                          currentReport,
                          reportID,
                          currentEvent,
                          dispatch,
                        );
                  }
                  closeModal();
                }
                setIsSubmitting(false);
                dispatch(fetchDraftReport(reportID));
              }}
              data-testid="save-event"
            >
              <StyledIconInButton
                size="medium"
                position="left"
                className="uil uil-save"
              />
              Save
            </SpinnerButton>
          </ButtonContainer>
        </BottomRow>
      </Body>
    </LegacyModal>
  );
};
